import React, { useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  Container, Box, Paper, Button, ButtonGroup, TextField, Table, TableBody,
  TableCell, TableHead, TableRow, Typography, Skeleton, Chip,
  TablePagination, TableContainer, Alert, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../components/ui/PageHeader';
import { ErrorRetry } from '../../components/ui/ErrorRetry';
import { statusStyle } from '../../theme/orderStatus';
import { useDebounce } from '../../hooks/useDebounce';
import {
  getReportSummary, listReportOrders, exportReportUrl, fetchReportRows, ReportOrderRow,
} from '../../api/pharmacyReports';
import { PharmacyOrderDetailPanel } from './PharmacyOrderDetailPanel';
import apiClient from '../../lib/axios';

type Preset = 'today' | '7' | '30' | 'custom';

/**
 * Boundaries are computed in the viewer's timezone and sent as full ISO
 * datetimes. Sending bare YYYY-MM-DD would be parsed server-side as UTC
 * midnight, which in the Dominican Republic (UTC-4) is 8pm the previous day.
 */
function rangeFor(preset: Preset, customFrom: string, customTo: string) {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date(end);

  // Subtract N-1 days so a preset spans exactly N calendar days inclusive of
  // today (today's end-of-day minus a start-of-day boundary N-1 days back is
  // N days of elapsed time; subtracting N days would span N+1 days).
  if (preset === 'today') start.setHours(0, 0, 0, 0);
  if (preset === '7') { start.setDate(start.getDate() - 6); start.setHours(0, 0, 0, 0); }
  if (preset === '30') { start.setDate(start.getDate() - 29); start.setHours(0, 0, 0, 0); }

  if (preset === 'custom' && customFrom && customTo) {
    const from = new Date(`${customFrom}T00:00:00`);
    const to = new Date(`${customTo}T23:59:59.999`);
    return { from: from.toISOString(), to: to.toISOString() };
  }

  return { from: start.toISOString(), to: end.toISOString() };
}

function formatDate(value: string | null): string | null {
  if (!value) return null;
  return new Date(value).toLocaleString();
}

export const PharmacyReports: React.FC = () => {
  const { t } = useTranslation();
  const [preset, setPreset] = useState<Preset>('7');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0); // MUI TablePagination is zero-based
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [printRows, setPrintRows] = useState<ReportOrderRow[] | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [isPreparing, setIsPreparing] = useState(false);

  const range = useMemo(
    () => rangeFor(preset, customFrom, customTo),
    [preset, customFrom, customTo],
  );

  // 300ms, matching the kanban's search box, so a manager typing a patient
  // name does not fire a request per keystroke.
  const debouncedSearch = useDebounce(search, 300);

  // The single object every consumer of the filters reads: both queries, the
  // export URL and the print fetch. Sharing it is what keeps the CSV, the
  // printout and the numbers on screen describing the same set of orders.
  const query = useMemo(
    () => ({ ...range, search: debouncedSearch.trim() || undefined }),
    [range, debouncedSearch],
  );

  // A custom range with either date field still empty is degenerate (from
  // === to, since rangeFor falls back to "now" for the unset boundary).
  // Don't fire a request for it — wait until the user has picked both dates.
  const rangeReady = preset !== 'custom' || (!!customFrom && !!customTo);

  // Reset to the first page whenever the range changes, so a narrower range
  // cannot leave the view stranded on a page that no longer exists.
  //
  // This adjusts state during render (React's documented pattern for
  // "derived state that must reset when a prop/input changes") rather than
  // in a useEffect. An effect runs after commit: the render that recomputes
  // `range` would already fire the orders query with the *new* range at the
  // *stale* page, and only the following render would correct it — one
  // wasted fetch plus a visible flash. Comparing identity here means the
  // reset lands before this render's query key is built, so only one
  // request (for the new range, at page 0) is ever issued. `range` is a
  // memoized object that only changes identity when preset/customFrom/
  // customTo change, so this does not fire on unrelated re-renders.
  const [prevRange, setPrevRange] = useState(range);
  if (prevRange !== range) {
    setPrevRange(range);
    setPage(0);
  }

  // Same reasoning for the search term: page 3 of the unfiltered result set
  // is meaningless once a term narrows it, and doing this in an effect would
  // fire one wasted request at the stale page first.
  const [prevSearch, setPrevSearch] = useState(debouncedSearch);
  if (prevSearch !== debouncedSearch) {
    setPrevSearch(debouncedSearch);
    setPage(0);
  }

  const summaryQuery = useQuery({
    queryKey: ['pharmacy-reports', 'summary', query],
    queryFn: () => getReportSummary(query),
    enabled: rangeReady,
  });

  const ordersQuery = useQuery({
    queryKey: ['pharmacy-reports', 'orders', query, page, rowsPerPage],
    queryFn: () => listReportOrders({ ...query, page: page + 1, limit: rowsPerPage }),
    enabled: rangeReady,
    // Without this, paging unmounts the table *and the pagination control*
    // in favour of a skeleton — the "next page" button disappears from under
    // the cursor mid-click.
    placeholderData: keepPreviousData,
  });

  const rows: ReportOrderRow[] = ordersQuery.data?.data ?? [];

  // The kanban and the detail panel both translate this; rendering the raw
  // enum here was the only English left on an otherwise Spanish page.
  const fulfillmentLabel = (type: ReportOrderRow['fulfillmentType']) =>
    t(type === 'PICKUP' ? 'kanban.pickup' : 'kanban.delivery') as string;

  // The API caps exports at 5,000 rows and answers with a 400 rather than a
  // truncated file. Only that failure warrants telling the manager to narrow
  // the range; a 500 or a dropped connection gets the generic load-failure
  // copy, because "reduce el rango" sends someone chasing a range problem
  // that does not exist.
  const isCapError = (error: unknown) =>
    (error as { response?: { status?: number } })?.response?.status === 400;

  const reportFailure = (error: unknown, context: string) => {
    if (!isCapError(error)) {
      console.error(context, error);
    }
    setExportError(
      isCapError(error) ? (t('reports.exportTooLarge') as string) : (t('reports.failedToLoad') as string),
    );
  };

  // The endpoint is authenticated, so the CSV is fetched through the same
  // axios client that carries the token and then saved from a blob — a plain
  // <a download> would hit the API without credentials.
  const handleExport = async () => {
    setExportError(null);
    setIsPreparing(true);
    try {
      const response = await apiClient.get(exportReportUrl(query), { responseType: 'blob' });
      const url = URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'reporte-pedidos.csv';
      // Firefox will not dispatch a download for an anchor that is not in the
      // document, and both Firefox and Safari abort a download whose object
      // URL is revoked in the same task. Neither throws — the button would
      // simply do nothing, which is invisible to jsdom and undiagnosable from
      // a pharmacy floor. Append, click, detach, revoke on a later tick.
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (error) {
      reportFailure(error, 'Failed to export report');
    } finally {
      setIsPreparing(false);
    }
  };

  const handlePrint = async () => {
    setExportError(null);
    setIsPreparing(true);
    try {
      const allRows = await fetchReportRows(query);
      // React 18 batches this setState and defers the DOM commit to a later
      // tick. window.print() runs on the very next line with no yield in
      // between and, per the HTML spec, blocks the event loop like a modal
      // dialog — so an unforced update would never be painted before the
      // page is captured, and the print output would omit the rows.
      // flushSync forces the commit synchronously before print() is called.
      //
      // Clearing the rows is deferred to `afterprint` rather than done on the
      // next line: print() blocking is a convention, not a guarantee, and in
      // any browser where it returns immediately a synchronous clear would
      // re-open the very race flushSync was added to close, from the far side.
      flushSync(() => setPrintRows(allRows));
      window.onafterprint = () => {
        setPrintRows(null);
        window.onafterprint = null;
      };
      window.print();
    } catch (error) {
      // A failed print must not leave the unstyled print table stranded on
      // screen below the report.
      setPrintRows(null);
      reportFailure(error, 'Failed to prepare report for printing');
    } finally {
      setIsPreparing(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
      <PageHeader
        title={t('reports.title')}
        subtitle={t('reports.subtitle') as string}
        action={
          <Box sx={{ display: 'flex', gap: 1 }} className="no-print">
            {/* Both are gated on the same rangeReady the queries use: half a
                custom range exports a header-only CSV and prints a blank
                sheet, with nothing to tell the manager why. */}
            <Button
              size="small" variant="outlined" onClick={handleExport}
              disabled={isPreparing || !rangeReady}
            >
              {isPreparing ? t('reports.exporting') : t('reports.export')}
            </Button>
            <Button
              size="small" variant="outlined" onClick={handlePrint}
              disabled={isPreparing || !rangeReady}
            >
              {t('reports.print')}
            </Button>
          </Box>
        }
      />

      {exportError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setExportError(null)}>
          {exportError}
        </Alert>
      )}

      <Paper
        sx={{ p: 2, mb: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <ButtonGroup size="small">
            <Button variant={preset === 'today' ? 'contained' : 'outlined'} onClick={() => setPreset('today')}>
              {t('reports.rangeToday')}
            </Button>
            <Button variant={preset === '7' ? 'contained' : 'outlined'} onClick={() => setPreset('7')}>
              {t('reports.range7')}
            </Button>
            <Button variant={preset === '30' ? 'contained' : 'outlined'} onClick={() => setPreset('30')}>
              {t('reports.range30')}
            </Button>
            <Button variant={preset === 'custom' ? 'contained' : 'outlined'} onClick={() => setPreset('custom')}>
              {t('reports.rangeCustom')}
            </Button>
          </ButtonGroup>

          {preset === 'custom' && (
            <>
              <TextField
                type="date" size="small" label={t('reports.from') as string}
                InputLabelProps={{ shrink: true }}
                value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
              />
              <TextField
                type="date" size="small" label={t('reports.to') as string}
                InputLabelProps={{ shrink: true }}
                value={customTo} onChange={(e) => setCustomTo(e.target.value)}
              />
            </>
          )}

          <TextField
            size="small"
            placeholder={t('reports.searchPlaceholder') as string}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flexGrow: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              sx: { bgcolor: 'background.paper' },
            }}
          />
        </Box>
      </Paper>

      {(summaryQuery.isError || ordersQuery.isError) && (
        <Box sx={{ mb: 2 }}>
          <ErrorRetry
            message={t('reports.failedToLoad') as string}
            onRetry={() => { summaryQuery.refetch(); ordersQuery.refetch(); }}
            isLoading={ordersQuery.isFetching}
          />
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mb: 3,
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' },
        }}
      >
        <SummaryTile label={t('reports.completed')} value={summaryQuery.data?.completedCount ?? '—'} />
        <SummaryTile label={t('reports.cancelled')} value={summaryQuery.data?.cancelledCount ?? '—'} />
        <SummaryTile label={t('reports.revenue')} value={summaryQuery.data?.revenue ?? '—'} />
        <SummaryTile
          label={t('reports.avgCompletion')}
          value={
            summaryQuery.data?.avgCompletionMinutes == null
              ? '—'
              : t('reports.minutes', { count: summaryQuery.data.avgCompletionMinutes })
          }
        />
        <SummaryTile
          label={t('reports.pickupVsDelivery')}
          value={
            summaryQuery.data
              ? `${summaryQuery.data.pickupCount} / ${summaryQuery.data.deliveryCount}`
              : '—'
          }
        />
      </Box>

      <Paper sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
        {/* Checked before every data branch: with an incomplete custom range
            no query has run, and (with keepPreviousData) the panel would
            otherwise show rows from the previous range as if they answered
            the half-entered one. */}
        {!rangeReady ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('reports.pickBothDates')}
            </Typography>
          </Box>
        ) : ordersQuery.isLoading ? (
          <Box sx={{ p: 2 }}>
            <Skeleton variant="rectangular" height={320} />
          </Box>
        ) : ordersQuery.isSuccess && rows.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('reports.empty')}
            </Typography>
          </Box>
        ) : ordersQuery.isSuccess ? (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('reports.colPlaced')}</TableCell>
                    <TableCell>{t('reports.colTerminal')}</TableCell>
                    <TableCell>{t('reports.colStatus')}</TableCell>
                    <TableCell>{t('reports.colPatient')}</TableCell>
                    <TableCell>{t('reports.colFulfillment')}</TableCell>
                    <TableCell align="right">{t('reports.colTotal')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const style = statusStyle(row.status);
                    return (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() => setSelectedOrderId(row.id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{formatDate(row.createdAt)}</TableCell>
                        <TableCell>
                          {formatDate(row.terminalAt) ?? (
                            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                              {t('reports.noTerminalDate')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small" label={style.label}
                            sx={{ bgcolor: style.bg, color: style.fg, fontWeight: 700 }}
                          />
                        </TableCell>
                        <TableCell>{row.patientName}</TableCell>
                        <TableCell>{fulfillmentLabel(row.fulfillmentType)}</TableCell>
                        <TableCell align="right">{row.totalAmount}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={ordersQuery.data?.total ?? 0}
              page={page}
              onPageChange={(_, next) => setPage(next)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage={t('reports.rowsPerPage')}
            />
          </>
        ) : null}
      </Paper>

      {/* Read-only: these orders are already finished, and a stray click on a
          status select would push a completed order back onto the live board
          and out of the report being read. */}
      <PharmacyOrderDetailPanel
        orderId={selectedOrderId}
        onClose={() => setSelectedOrderId(null)}
        readOnly
      />

      {printRows && (
        <Box className="printable-report">
          <Typography variant="h6">{t('reports.title')}</Typography>
          <Typography variant="body2">
            {t('reports.printRange', {
              from: new Date(range.from).toLocaleDateString(),
              to: new Date(range.to).toLocaleDateString(),
            })}
          </Typography>
          <Typography variant="caption">
            {t('reports.printedOn', { date: new Date().toLocaleString() })}
          </Typography>
          <Table size="small">
            {/* The printout is the end-of-month artifact. Without headers it
                is five unlabelled columns, two of them adjacent dates. */}
            <TableHead>
              <TableRow>
                <TableCell>{t('reports.colPlaced')}</TableCell>
                <TableCell>{t('reports.colTerminal')}</TableCell>
                <TableCell>{t('reports.colStatus')}</TableCell>
                <TableCell>{t('reports.colPatient')}</TableCell>
                <TableCell>{t('reports.colFulfillment')}</TableCell>
                <TableCell align="right">{t('reports.colTotal')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {printRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{formatDate(row.createdAt)}</TableCell>
                  <TableCell>{formatDate(row.terminalAt) ?? '—'}</TableCell>
                  <TableCell>{statusStyle(row.status).label}</TableCell>
                  <TableCell>{row.patientName}</TableCell>
                  <TableCell>{fulfillmentLabel(row.fulfillmentType)}</TableCell>
                  <TableCell align="right">{row.totalAmount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Container>
  );
};

const SummaryTile: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Paper
    sx={{
      p: 2,
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: 'none',
    }}
  >
    <Typography
      sx={{
        fontSize: '0.68rem',
        fontWeight: 700,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: 'text.secondary',
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ mt: 0.5, fontSize: '1.5rem', fontWeight: 700, color: 'text.headline', fontVariant: 'tabular-nums' }}
    >
      {value}
    </Typography>
  </Paper>
);
